import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Icon, IconButton, ListItemText } from '@mui/material';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import VerticalItem from '../VerticalItem';
import Box from '@mui/material/Box';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { checkPermission } from '@projectK/helpers';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { useThemeContext } from '@projectK/context/ThemeContextProvider';
import { useSidebarContext } from '@projectK/context/SidebarContextProvider';
import VerticalCollapseItem from './VerticalCollapseItem';
import { allowMultiLanguage } from '@projectK/constants/AppConst';

const needsToBeOpened = (pathname, item) => {
  return pathname && isUrlInChildren(item, pathname);
};

const isUrlInChildren = (parent, url) => {
  if (!parent.children) {
    return false;
  }

  for (const child of parent.children) {
    if (child.children && isUrlInChildren(child, url)) {
      return true;
    }
    if (child.url === url || url.includes(child.url)) {
      return true;
    }
  }

  return false;
};

const VerticalCollapse = ({
  item,
  level,
  activePath,
  currentPath,
  onToggle,
}) => {
  const { theme } = useThemeContext();
  const { sidebarTextColor } = useSidebarContext();
  const { pathname } = useLocation();
  const isOpen = activePath?.includes(item.id);
  const handleClick = () => {
    // Get full path hierarchy
    const fullPath = [...currentPath, item.id];
    onToggle(item.id, fullPath);
  };

  // Auto-open when route matches
  useEffect(() => {
    if (needsToBeOpened(pathname, item) && !isOpen) {
      onToggle(item.id, currentPath);
    }
  }, [pathname, item]);

  const { user } = useAuthUser();

  const hasPermission = useMemo(
    () => checkPermission(item.permittedRole, user.role),
    [item.permittedRole, user.role],
  );

  const getIconName = () => {
    if (isOpen) return 'expand_more';
    return theme.direction === 'ltr' ? 'chevron_right' : 'chevron_left';
  };

  if (!hasPermission) return null;

  return (
    <>
      <VerticalCollapseItem
        level={level}
        sidebarTextColor={sidebarTextColor}
        button
        component='div'
        className={clsx('menu-vertical-collapse', isOpen && 'open')}
        onClick={handleClick}
        // className={clsx({ open: isOpen })}
      >
        {item.icon && (
          <Box component='span'>
            <Icon
              sx={{ mr: 4 }}
              color='action'
              className={clsx('nav-item-icon')}
            >
              {item.icon}
            </Icon>
          </Box>
        )}
        <ListItemText
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 14,
          }}
          className='nav-item-content'
          classes={{ primary: clsx('nav-item-text') }}
          primary={
            allowMultiLanguage ? (
              <IntlMessages id={item.messageId} />
            ) : (
              item.title
            )
          }
        />
        <IconButton
          className='nav-item-icon-arrow-btn'
          sx={{ p: 0, mr: 0.75 }}
          disableRipple
          size='large'
        >
          <Icon className='nav-item-icon-arrow' color='inherit'>
            {getIconName()}
          </Icon>
        </IconButton>
      </VerticalCollapseItem>

      {item.children && (
        <Collapse in={isOpen} className='collapse-children'>
          {item?.children.map((data) => (
            <React.Fragment key={data.id}>
              {data.type === 'collapse' && (
                <VerticalCollapse
                  key={data.id}
                  item={data}
                  level={level + 1}
                  activePath={activePath}
                  currentPath={[...currentPath, item.id]}
                  onToggle={onToggle}
                />
              )}
              {data.type === 'item' && (
                <VerticalItem item={data} level={level + 1} />
              )}
            </React.Fragment>
          ))}
        </Collapse>
      )}
    </>
  );
};

VerticalCollapse.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    permittedRole: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    children: PropTypes.array,
    messageId: PropTypes.string,
    type: PropTypes.string,
  }),
  level: PropTypes.number,
  currentPath: PropTypes.array,
  activePath: PropTypes.array,
  onToggle: PropTypes.func,
};
VerticalCollapse.defaultProps = {};

export default React.memo(VerticalCollapse);
